// Butterfly valves (owner, 2026-10-21: "add in a set of butterfly valves that we can also insert for turning things on
// and off, and have it so we can set them to open or closed. And they can be either lever or gearboxed.").
//
// 3.22.0: ERA VALVES, drawn from the ERA catalogue (owner, 2026-10-21: "I need the butter fly valves and gearbox driven
// valves ensure you include the flanges, use the era catalogue for drawings and measurements, and ensure I can set the
// valves open and closed percentage ... we use them a lot"). Catalogue: "PVC valves from ERA" (Ozcrete, Suppliers\ERA),
// "Plastic valves and fittings", pages 015 to 017:
//   - UBV01 butterfly valve with a lever, DN50 to DN200: D1 body, D2 bolt circle (DIN), D3 bore (the disc), D4 the notch
//     plate under the lever, L the lever, H1 the bottom of the body to the top of the lever, H2 the centre to the top of
//     the neck, W1 face to face, n-Øe the bolt holes (DIN);
//   - UBV02 the same valve between two flanges: D the flange, the bolts (number and M size) for each size;
//   - UBV03 butterfly valve, gear type (gearbox and handwheel), DN100 to DN400: ØD body, P.C.D ØD1 (DIN), Ød1 the disc,
//     L face to face, Ø1 the handwheel, L1 the stem axis to the handwheel, H1 the centre to the gearbox, H the bottom of
//     the body to the top of the gearbox, M x N the gearbox (M along the pipe), N-Øe the bolt holes (DIN), PN;
//   - UVF01 Van Stone flange (the flange on each side): H the flange ring, h the stub from the valve face, d4 the socket.
// The catalogue has no lever valve above DN200 and no gearbox below DN100: TraceCAD has none either.
//
// NOT IN THE CATALOGUE, so drawn to proportion and listed in `estimated`: the lever width (0.14 x L), the disc at its
// hub (0.16 x the disc), the nuts (1.5 x M across, 0.8 x M thick), the handwheel thickness, the neck and the top plate.
// Also: the Van Stone flange of DN125 (its catalogue row is not clear: h 74, H 22, socket 142.4 are used), DN350 and
// DN400 (no row: drawn to proportion), the gearbox height of DN350 and DN400 (their H1 does not fit their H), and the
// face to face of DN300 with flanges (UBV02 prints 180; the valve, UBV03, is 108 and is used).
//
// OPEN PERCENTAGE (3.22.0): 100 % open, the disc along the pipe; 0 % closed, the disc across it. Between them the disc
// (and the lever, and the position bar of the gearbox) is drawn at (100 - %) x 0.9 degrees from the pipe.
//
// THE DRAWING. Two views. Both have the flow axis left (end 1) to right (end 2), and the ends at the outer ends of the
// flange sockets, so the two views have the same length and a pipe stays on when the view or the position changes:
//   PLAN (seen from above; the stem points at you): the body, the two flange rings and their sockets, two bolts with
//     nuts, the disc as a dashed diamond at its angle. LEVER: the notch plate and the lever at the angle of the disc
//     (along the pipe = open). GEARBOX: the box on the stem, the shaft and the handwheel edge on beside the pipe, and the
//     position bar on the box at the angle of the disc.
//   SIDE (an elevation; the stem points up): the body, the flanges, the neck, then the top plate and the lever (its length
//     as seen from the side), or the gearbox with the handwheel face on. The disc, dashed: open = a circle (seen face on),
//     closed = an upright bar (seen edge on), between them an ellipse. A dash-dot line on the stem.
import type { Vec } from "../core/geometry";
import type { Entity, Style } from "../doc/entities";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";
import { CENTRE, HIDDEN, OUTLINE } from "./fittings";

export const BUTTERFLY_CATEGORY = "Pipe fittings · Butterfly valves";
export const ERA_SOURCE = "ERA catalogue \"Plastic valves and fittings\", pages 015-017: UBV01, UBV02, UBV03, UVF01";

export type ButterflyOperator = "lever" | "gearbox";
export type ButterflyView = "plan" | "side";

/** ERA UBV01, the butterfly valve with a lever (mm). */
export interface EraLever {
  D1: number;
  /** Bolt circle, DIN. */
  D2: number;
  /** Bore: the disc. */
  D3: number;
  /** The notch plate under the lever. */
  D4: number;
  /** The lever. */
  L: number;
  /** The bottom of the body to the top of the lever. */
  H1: number;
  /** The centre to the top of the neck. */
  H2: number;
  /** Face to face. */
  W1: number;
  /** Bolt holes, DIN, "number-diameter". */
  holes: string;
}

/** ERA UBV03, the butterfly valve, gear type (mm). */
export interface EraGear {
  /** Body. */
  D: number;
  /** Bolt circle, DIN. */
  PCD: number;
  /** The disc. */
  d1: number;
  /** Face to face. */
  L: number;
  /** The handwheel. */
  wheel: number;
  /** The stem axis to the handwheel. */
  L1: number;
  /** The centre to the gearbox. */
  H1: number;
  /** The bottom of the body to the top of the gearbox. */
  H: number;
  /** The gearbox: M along the pipe, N across it. */
  M: number;
  N: number;
  holes: string;
  /** Pressure rating, psi. */
  psi: number;
}

/** The flange on each side (ERA UVF01 Van Stone flange; the bolts of UBV02), mm. */
export interface EraFlange {
  /** The flange (UBV02 D). */
  od: number;
  /** The flange ring (UVF01 H). */
  ring: number;
  /** The stub from the valve face to the end of its socket (UVF01 h). */
  stub: number;
  /** The socket, outside (UVF01 d4). */
  socket: number;
  bolts: number;
  /** Bolt size, M. */
  M: number;
  /** True: no clear catalogue row; drawn to proportion. */
  estimated: boolean;
}

export interface ButterflyRow {
  DN: number;
  inch: string;
  /** The pipe outside diameter for this size (UBV02 FLANGE), mm. */
  pipeOD: number;
  lever?: EraLever;
  gear?: EraGear;
  flange: EraFlange;
}

const fl = (od: number, ring: number, stub: number, socket: number, bolts: number, M: number, estimated = false): EraFlange => ({ od, ring, stub, socket, bolts, M, estimated });

/** The ERA catalogue rows, DN50 to DN400 (see the note at the top). */
export const BUTTERFLY_ROWS: readonly ButterflyRow[] = [
  { DN: 50, inch: "2\"", pipeOD: 63, lever: { D1: 160.1, D2: 120, D3: 55.6, D4: 99.7, L: 189.7, H1: 223.3, H2: 98.3, W1: 44, holes: "4-20" }, flange: fl(160, 20.5, 43.5, 74.6, 4, 16) },
  { DN: 65, inch: "2-1/2\"", pipeOD: 75, lever: { D1: 179.8, D2: 141.2, D3: 68.7, D4: 99.7, L: 189, H1: 247, H2: 111.6, W1: 48.3, holes: "4-18" }, flange: fl(180, 24.8, 49, 90.6, 4, 16) },
  { DN: 80, inch: "3\"", pipeOD: 90, lever: { D1: 195, D2: 156.4, D3: 83.9, D4: 99.7, L: 238.5, H1: 282.6, H2: 132.5, W1: 51.9, holes: "8-20" }, flange: fl(196, 26.4, 54.9, 107, 8, 16) },
  {
    DN: 100, inch: "4\"", pipeOD: 110,
    lever: { D1: 228.1, D2: 183, D3: 103.2, D4: 99.7, L: 239.5, H1: 319.4, H2: 155.4, W1: 54.5, holes: "8-20" },
    gear: { D: 228, PCD: 180, d1: 98, L: 54, wheel: 145, L1: 120, H1: 140, H: 340, M: 120, N: 90, holes: "8-19", psi: 150 },
    flange: fl(228, 28.5, 64.8, 131.8, 8, 16)
  },
  {
    DN: 125, inch: "5\"", pipeOD: 140,
    lever: { D1: 257, D2: 210.9, D3: 129.5, D4: 139, L: 309.8, H1: 368, H2: 170.3, W1: 66.5, holes: "8-21.5" },
    gear: { D: 258, PCD: 210, d1: 122, L: 64, wheel: 280, L1: 200, H1: 168, H: 385, M: 180, N: 100, holes: "8-19", psi: 150 },
    flange: fl(258, 22, 74, 142.4, 8, 16, true)
  },
  {
    DN: 150, inch: "6\"", pipeOD: 160,
    lever: { D1: 285.4, D2: 238.5, D3: 151.1, D4: 140, L: 309.8, H1: 400.8, H2: 187.5, W1: 73.1, holes: "8-24" },
    gear: { D: 287, PCD: 240, d1: 146, L: 70, wheel: 280, L1: 200, H1: 181, H: 417, M: 180, N: 100, holes: "8-23", psi: 150 },
    flange: fl(287, 32, 85, 189.2, 8, 20)
  },
  {
    DN: 200, inch: "8\"", pipeOD: 225,
    lever: { D1: 342.6, D2: 292.4, D3: 198.6, D4: 140, L: 309.8, H1: 473, H2: 230.1, W1: 92, holes: "8-24" },
    gear: { D: 344, PCD: 295, d1: 199, L: 88, wheel: 280, L1: 200, H1: 215, H: 487, M: 180, N: 100, holes: "8-23", psi: 150 },
    flange: fl(344, 36.2, 119, 237, 8, 20)
  },
  { DN: 250, inch: "10\"", pipeOD: 280, gear: { D: 412, PCD: 350, d1: 255, L: 98, wheel: 280, L1: 220, H1: 260, H: 551, M: 180, N: 130, holes: "12-25", psi: 100 }, flange: fl(412, 34.5, 135, 305.9, 12, 20) },
  { DN: 300, inch: "12\"", pipeOD: 315, gear: { D: 460, PCD: 400, d1: 302, L: 108, wheel: 280, L1: 220, H1: 280, H: 595, M: 180, N: 130, holes: "12-25", psi: 100 }, flange: fl(460, 36.5, 162, 361.5, 12, 20) },
  { DN: 350, inch: "14\"", pipeOD: 350, gear: { D: 500, PCD: 460, d1: 345, L: 88, wheel: 380, L1: 280, H1: 410, H: 670, M: 250, N: 220, holes: "16-25", psi: 60 }, flange: fl(500, 38, 175, 400, 16, 20, true) },
  { DN: 400, inch: "16\"", pipeOD: 400, gear: { D: 565, PCD: 515, d1: 395, L: 98, wheel: 380, L1: 280, H1: 490, H: 770, M: 250, N: 220, holes: "16-27", psi: 60 }, flange: fl(565, 40, 190, 456, 16, 24, true) }
];

/** What a placed butterfly valve says about itself (group meta.butterfly): the Inspector reads and changes it. */
export interface ButterflyInfo {
  DN: number;
  operator: ButterflyOperator;
  /** 0 = closed, 100 = fully open (3.22.0). */
  openPct: number;
  /** openPct > 0 (kept for drawings made before 3.22.0). */
  open: boolean;
  view: ButterflyView;
  maker: "ERA";
  /** "UBV01 with two UVF01 Van Stone flanges" */
  item: string;
  /** "Butterfly valve, PVC, lever, with two Van Stone flanges" */
  model: string;
  /** Pressure rating, psi. */
  psi: number;
  /** Face to face of the valve, mm. */
  L: number;
  /** End to end with the two flanges, mm. */
  length: number;
  bolts: string;
  source: string;
  /** The parts that are drawn to proportion, not from the catalogue. */
  estimated: string[];
}

export interface ButterflySpec {
  operator: ButterflyOperator;
  /** 0 = closed, 100 = fully open. */
  openPct: number;
  view: ButterflyView;
}

/** The row for a pipe size (DN), or undefined. */
export const butterflyRowFor = (dn: number): ButterflyRow | undefined => BUTTERFLY_ROWS.find((r) => r.DN === dn);

/** True when the catalogue has this operator in this size. */
export const hasOperator = (r: ButterflyRow, op: ButterflyOperator): boolean => (op === "lever" ? !!r.lever : !!r.gear);

/** The sizes of an operator, for messages: "DN50 to DN200". */
export const operatorSizes = (op: ButterflyOperator): string => {
  const dns = BUTTERFLY_ROWS.filter((r) => hasOperator(r, op)).map((r) => r.DN);
  return `DN${dns[0]} to DN${dns[dns.length - 1]}`;
};

/** A whole number from 0 to 100. */
export const clampPct = (v: number): number => (Number.isFinite(v) ? Math.min(100, Math.max(0, Math.round(v))) : 100);

/** "open", "closed" or "40 % open" */
export const positionWords = (pct: number): string => (pct >= 100 ? "open" : pct <= 0 ? "closed" : `${clampPct(pct)} % open`);

/** "Butterfly valve · DN100 · lever · open · plan" */
export const butterflyName = (dn: number, s: ButterflySpec): string => `Butterfly valve · DN${dn} · ${s.operator} · ${positionWords(s.openPct)} · ${s.view}`;

/** The angle of the disc from the pipe, degrees: 0 open, 90 closed. */
export const discAngle = (pct: number): number => ((100 - clampPct(pct)) * 90) / 100;

const round = (v: number) => Math.round(v * 1000) / 1000;
const rp = (p: Vec): Vec => ({ x: round(p.x), y: round(p.y) });

/** Width and height of the drawn parts (arcs sampled along their sweep), mm. */
function sizeOf(entities: Entity[]): { w: number; h: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const add = (p: Vec) => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  };
  for (const e of entities) {
    if (e.type === "polyline") e.points.forEach(add);
    else if (e.type === "arc") for (let i = 0; i <= 16; i++) add({ x: e.center.x + e.r * Math.cos(((e.startDeg + (e.sweepDeg * i) / 16) * Math.PI) / 180), y: e.center.y + e.r * Math.sin(((e.startDeg + (e.sweepDeg * i) / 16) * Math.PI) / 180) });
  }
  return { w: Math.round((maxX - minX) * 10) / 10, h: Math.round((maxY - minY) * 10) / 10 };
}

/** Face to face of the valve of an operator, mm. */
const faceToFace = (r: ButterflyRow, op: ButterflyOperator): number => (op === "lever" ? r.lever!.W1 : r.gear!.L);

/** End to end: the valve and the stub of each flange, mm. */
export const butterflyLength = (r: ButterflyRow, op: ButterflyOperator = r.lever ? "lever" : "gearbox"): number => round(faceToFace(r, op) + 2 * r.flange.stub);

/**
 * One butterfly valve as a library item, centred on the stem (0,0), the flow axis along X: end 1 on the left, end 2 on
 * the right. Throws when the catalogue has no such operator in this size.
 */
export function makeButterflyItem(r: ButterflyRow, s: ButterflySpec): LibraryItem {
  if (!hasOperator(r, s.operator)) throw new Error(`butterfly valves: ERA has no ${s.operator} valve in DN${r.DN} (${operatorSizes(s.operator)})`);
  const pct = clampPct(s.openPct);
  const key = `butterfly-${r.DN}-${s.operator}-${pct}-${s.view}`;
  let n = 0;
  const entities: Entity[] = [];
  const line = (pts: Vec[], closed = false, style: Style = OUTLINE) =>
    entities.push({ id: `${key}-${++n}`, type: "polyline", layerId: "fittings", locked: false, hidden: false, points: pts.map(rp), closed, style: structuredClone(style) });
  const rect = (x0: number, y0: number, x1: number, y1: number, style: Style = OUTLINE) =>
    line([{ x: Math.min(x0, x1), y: Math.min(y0, y1) }, { x: Math.max(x0, x1), y: Math.min(y0, y1) }, { x: Math.max(x0, x1), y: Math.max(y0, y1) }, { x: Math.min(x0, x1), y: Math.max(y0, y1) }], true, style);
  const arc = (c: Vec, rad: number, start: number, sweep: number, style: Style = OUTLINE) =>
    entities.push({ id: `${key}-${++n}`, type: "arc", layerId: "fittings", locked: false, hidden: false, center: rp(c), r: round(rad), startDeg: start, sweepDeg: sweep, style: structuredClone(style) });
  // a whole circle as two half arcs (a whole-circle arc has the same start and end, and draws nothing in a picture)
  const circle = (c: Vec, rad: number, style: Style = OUTLINE) => {
    arc(c, rad, 0, 180, style);
    arc(c, rad, 180, 180, style);
  };
  // turned by the disc angle, clockwise on the screen from +X toward -Y (open: along the pipe; closed: across it)
  const a = (discAngle(pct) * Math.PI) / 180;
  const turn = (p: Vec): Vec => ({ x: p.x * Math.cos(a) + p.y * Math.sin(a), y: -p.x * Math.sin(a) + p.y * Math.cos(a) });

  const lever = s.operator === "lever" ? r.lever! : null;
  const gear = s.operator === "gearbox" ? r.gear! : null;
  const f = r.flange;
  const W = faceToFace(r, s.operator);
  const body = lever ? lever.D1 : gear!.D;
  const pcd = lever ? lever.D2 : gear!.PCD;
  const disc = lever ? lever.D3 : gear!.d1;
  const half = W / 2;
  const ring = half + f.ring; // the outer face of each flange ring
  const end = half + f.stub; // each end of the valve: the end of the flange socket
  const plan = s.view === "plan";
  const estimated: string[] = ["disc at its hub", "nuts"];
  if (f.estimated) estimated.push(`Van Stone flange of DN${r.DN}`);

  // the body, the two flange rings and their sockets
  rect(-half, -body / 2, half, body / 2);
  for (const k of [-1, 1]) {
    rect(k * half, -f.od / 2, k * ring, f.od / 2);
    rect(k * ring, -f.socket / 2, k * end, f.socket / 2);
  }
  // two bolts through the body and both rings, a nut on each end
  const nutL = Math.round(0.8 * f.M);
  const nutH = Math.round(1.5 * f.M);
  for (const k of [-1, 1]) {
    const y = (k * pcd) / 2;
    line([{ x: -(ring + nutL), y }, { x: ring + nutL, y }]);
    rect(-(ring + nutL), y - nutH / 2, -ring, y + nutH / 2);
    rect(ring, y - nutH / 2, ring + nutL, y + nutH / 2);
  }
  // the flow axis
  line([{ x: -end, y: 0 }, { x: end, y: 0 }], false, CENTRE);

  const discT = Math.max(8, Math.round(0.16 * disc)); // the disc at its hub
  let top = 0;
  if (plan) {
    // the disc, dashed (inside the body), at its angle
    line([{ x: -disc / 2, y: 0 }, { x: 0, y: -discT / 2 }, { x: disc / 2, y: 0 }, { x: 0, y: discT / 2 }].map(turn), true, HIDDEN);
    if (lever) {
      // the notch plate and the lever at the angle of the disc (the stem is near the head end of the lever)
      const w = Math.round(0.14 * lever.L);
      circle({ x: 0, y: 0 }, lever.D4 / 2);
      const back = Math.round(0.06 * lever.L);
      const tip = lever.L - back;
      line([{ x: -back, y: -w / 2 }, { x: tip, y: -w * 0.4 }, { x: tip, y: w * 0.4 }, { x: -back, y: w / 2 }].map(turn), true);
      estimated.push("lever width");
    } else {
      // the gearbox on the stem (M along the pipe, N across), the shaft and the handwheel edge on, beside the pipe
      const g = gear!;
      rect(-g.M / 2, -g.N / 2, g.M / 2, g.N / 2);
      const wheelT = Math.max(16, Math.round(0.08 * g.wheel));
      line([{ x: -8, y: -g.N / 2 }, { x: -8, y: -(g.L1 - wheelT) }]);
      line([{ x: 8, y: -g.N / 2 }, { x: 8, y: -(g.L1 - wheelT) }]);
      rect(-g.wheel / 2, -g.L1, g.wheel / 2, -(g.L1 - wheelT));
      // the position bar on the box at the angle of the disc
      const pl = Math.round(0.6 * Math.min(g.M, g.N));
      line([{ x: -pl / 2, y: -5 }, { x: pl / 2, y: -5 }, { x: pl / 2, y: 5 }, { x: -pl / 2, y: 5 }].map(turn), true);
      estimated.push("handwheel thickness");
    }
  } else {
    // SIDE: the disc, dashed: open = face on (a circle), closed = edge on (an upright bar), between = an ellipse
    const hx = Math.max(discT / 2, (disc / 2) * Math.cos(a));
    const ell: Vec[] = [];
    for (let i = 0; i < 36; i++) ell.push({ x: hx * Math.cos((i * Math.PI) / 18), y: (disc / 2) * Math.sin((i * Math.PI) / 18) });
    line(ell, true, HIDDEN);
    if (lever) {
      // the neck, the top plate, the lever: its length as seen from the side
      top = lever.H1 - lever.D1 / 2;
      const plateT = 8;
      const nw = Math.max(0.4 * W, 16);
      rect(-nw, -(lever.H2 - plateT), nw, -body / 2);
      rect(-lever.D4 / 2, -lever.H2, lever.D4 / 2, -(lever.H2 - plateT));
      const w = Math.round(0.14 * lever.L);
      const back = Math.round(0.06 * lever.L);
      const tip = lever.L - back;
      const x0 = Math.min(-back * Math.cos(a) - (w / 2) * Math.sin(a), -w / 2);
      const x1 = Math.max(tip * Math.cos(a) + (w / 2) * Math.sin(a), w / 2);
      const barT = Math.min(24, top - lever.H2);
      rect(x0, -top, x1, -(top - barT));
      // the head on the stem, from the plate to the lever
      rect(-0.3 * lever.D4, -(top - barT), 0.3 * lever.D4, -lever.H2);
      estimated.push("lever width", "neck", "top plate");
    } else {
      const g = gear!;
      top = g.H - g.D / 2;
      // the gearbox: from its catalogue height (H - ØD/2 - H1); where that does not fit (DN350, DN400), 0.5 x M
      const fromCat = top - g.H1;
      const boxH = fromCat >= 60 ? fromCat : Math.round(0.5 * g.M);
      if (fromCat < 60) estimated.push("gearbox height");
      const nw = Math.max(0.4 * W, 16);
      rect(-nw, -(top - boxH), nw, -body / 2);
      rect(-g.M / 2, -top, g.M / 2, -(top - boxH));
      // the handwheel, face on (its shaft points at you): rim, inner rim, hub and three spokes
      const c = { x: 0, y: -(top - boxH / 2) };
      const R = g.wheel / 2;
      const rim = Math.max(8, Math.round(0.06 * g.wheel));
      circle(c, R);
      circle(c, R - rim);
      circle(c, rim);
      for (const deg of [90, 210, 330]) {
        const t = (deg * Math.PI) / 180;
        line([{ x: c.x + rim * Math.cos(t), y: c.y + rim * Math.sin(t) }, { x: c.x + (R - rim) * Math.cos(t), y: c.y + (R - rim) * Math.sin(t) }]);
      }
      estimated.push("neck", "handwheel rim and spokes");
    }
    // the stem line
    line([{ x: 0, y: body / 2 + 10 }, { x: 0, y: -(top + 10) }], false, CENTRE);
  }

  const ports: FittingPortData[] = [
    { p: { x: -end, y: 0 }, dir: { x: -1, y: 0 }, size: r.DN },
    { p: { x: end, y: 0 }, dir: { x: 1, y: 0 }, size: r.DN }
  ];
  const info: ButterflyInfo = {
    DN: r.DN,
    operator: s.operator,
    openPct: pct,
    open: pct > 0,
    view: s.view,
    maker: "ERA",
    item: `${lever ? "UBV01" : "UBV03"} with two UVF01 Van Stone flanges`,
    model: `Butterfly valve, PVC, ${lever ? "lever" : "gearbox with handwheel"}, with two Van Stone flanges`,
    psi: lever ? 200 : gear!.psi,
    L: W,
    length: round(2 * end),
    bolts: `${f.bolts} x M${f.M}, bolt circle ${pcd} mm (DIN)`,
    source: ERA_SOURCE,
    estimated: [...new Set(estimated)]
  };
  entities.push({
    id: `${key}-group`,
    type: "group",
    layerId: "fittings",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: { fitting: { kind: "butterfly", size: r.DN }, butterfly: info, pivot: rp(ports[0]!.p), ports }
  });
  return {
    id: `builtin:${key}`,
    name: butterflyName(r.DN, { ...s, openPct: pct }),
    category: BUTTERFLY_CATEGORY,
    entities,
    size: sizeOf(entities),
    units: "mm",
    updatedAt: "2026-10-21T00:00:00.000Z"
  };
}

let cache: LibraryItem[] | null = null;
/** The library: lever DN50 to DN200 and gearbox DN100 to DN400, plan and side, placed open. 30 items. */
export function builtInButterflyValves(): LibraryItem[] {
  if (cache) return cache;
  const out: LibraryItem[] = [];
  for (const r of BUTTERFLY_ROWS) {
    for (const operator of ["lever", "gearbox"] as const) {
      if (!hasOperator(r, operator)) continue;
      for (const view of ["plan", "side"] as const) out.push(makeButterflyItem(r, { operator, openPct: 100, view }));
    }
  }
  cache = out;
  return out;
}

/** A butterfly valve item for pipe size `dn`, or null when the catalogue has none (or not with that operator). */
export function butterflyItemFor(dn: number, s: ButterflySpec): LibraryItem | null {
  const r = butterflyRowFor(dn);
  return r && hasOperator(r, s.operator) ? makeButterflyItem(r, s) : null;
}
